import type { HttpTransport, TransportResponse } from '@mangovoice/api-client'

import type { ScheduleCreate } from '../types'

type JsonObject = Record<string, unknown>

export type AssetScheduleCreate = ScheduleCreate & {
  pbx_uuid: string
}

const ASSET_SCHEDULE_EVENT_PATH =
  /^\/assets\/v1\/schedules\/[^/]+\/events(?:\/[^/]+)?$/
const NULLABLE_RECURRENCE_FIELDS = ['rrule', 'rdate', 'exdate'] as const

const isJsonObject = (value: unknown): value is JsonObject =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const legacyIdFrom = (value: JsonObject): number | undefined => {
  const legacyId = value.legacy_id
  if (typeof legacyId === 'number') return legacyId
  if (typeof legacyId !== 'string' || !/^\d+$/.test(legacyId)) return undefined
  return Number(legacyId)
}

export const buildAssetScheduleCreatePayload = (
  data: ScheduleCreate,
  pbxUuid: string,
): AssetScheduleCreate => ({
  ...data,
  pbx_uuid: pbxUuid,
})

export const normalizeAssetSchedule = (value: unknown): unknown => {
  if (!isJsonObject(value)) return value

  const legacyId = legacyIdFrom(value)
  if (typeof value.id !== 'string') return value

  return {
    ...value,
    ...(legacyId === undefined ? {} : { legacy_id: legacyId }),
  }
}

export const normalizeAssetScheduleEvent = (value: unknown): unknown => {
  if (!isJsonObject(value)) return value

  const legacyId = legacyIdFrom(value)
  if (typeof value.id !== 'string') return value

  const normalizeEnum = (field: unknown) =>
    typeof field === 'string' ? field.toUpperCase() : field
  const fbtype = normalizeEnum(value.fbtype)
  const usesRecurringWeeklyHoursBounds =
    fbtype === 'FREE' && Boolean(value.rrule) && !value.rdate && !value.exdate

  return {
    ...value,
    ...(usesRecurringWeeklyHoursBounds
      ? { start: undefined, end: undefined }
      : {}),
    ...(legacyId === undefined ? {} : { legacy_id: legacyId }),
    fbtype,
    ui_component: normalizeEnum(value.ui_component),
    switchboard_routing: normalizeEnum(value.switchboard_routing),
  }
}

export const normalizeAssetScheduleEventRequest = (
  url: string,
  data: unknown,
): unknown => {
  if (!ASSET_SCHEDULE_EVENT_PATH.test(url) || !isJsonObject(data)) return data

  const normalized = { ...data }
  for (const field of NULLABLE_RECURRENCE_FIELDS) {
    if (normalized[field] === null) normalized[field] = ''
  }

  return normalized
}

export const normalizeAssetSchedulePayload = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(normalizeAssetScheduleEvent)
  if (!isJsonObject(value)) return value

  if (Array.isArray(value.results)) {
    return {
      ...value,
      results: value.results.map(normalizeAssetSchedule),
    }
  }

  // The current Asset wire contract uses schedule_id to distinguish Events;
  // parent Schedule payloads do not include it.
  return 'schedule_id' in value
    ? normalizeAssetScheduleEvent(value)
    : normalizeAssetSchedule(value)
}

const normalizeAssetResponse = <T>(
  response: TransportResponse<T>,
): TransportResponse<T> => {
  if (!response.ok || response.data === undefined) return response

  return {
    ...response,
    data: normalizeAssetSchedulePayload(response.data) as T,
  }
}

/**
 * Adapts the API Gateway transport to Asset's Schedule wire contract.
 *
 * The shared UI model accepts Mango integer IDs and Asset UUIDs. This adapter
 * is deliberately Asset-only: it normalizes Asset response fields, preserves
 * server-side list search/pagination, and never translates a UUID into a
 * legacy integer identity.
 */
export const createAssetScheduleTransport = (
  gateway: HttpTransport,
): HttpTransport => ({
  get: async <T>(
    url: string,
    params?: Record<string, unknown>,
    config?: Parameters<HttpTransport['get']>[2],
  ) => normalizeAssetResponse(await gateway.get<T>(url, params, config)),
  post: async <T>(
    url: string,
    data?: unknown,
    config?: Parameters<HttpTransport['post']>[2],
  ) =>
    normalizeAssetResponse(
      await gateway.post<T>(
        url,
        normalizeAssetScheduleEventRequest(url, data),
        config,
      ),
    ),
  put: async <T>(
    url: string,
    data?: unknown,
    config?: Parameters<HttpTransport['put']>[2],
  ) =>
    normalizeAssetResponse(
      await gateway.put<T>(
        url,
        normalizeAssetScheduleEventRequest(url, data),
        config,
      ),
    ),
  patch: async <T>(
    url: string,
    data?: unknown,
    config?: Parameters<HttpTransport['patch']>[2],
  ) =>
    normalizeAssetResponse(
      await gateway.patch<T>(
        url,
        normalizeAssetScheduleEventRequest(url, data),
        config,
      ),
    ),
  delete: <T>(
    url: string,
    params?: Record<string, unknown>,
    config?: Parameters<HttpTransport['delete']>[2],
  ) => gateway.delete<T>(url, params, config),
})
